import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db, DEFAULT_STORE_ID } from "../../../../firebase/firebase";
import { useUser } from "../../../../context/UserContext";
import { useLanguage } from "../../../../context/LanguageContext";
import {
  getLocalizedCategoryName,
  getLocalizedCategories,
  getActiveStoreCategories,
} from "../../../../firebase/category-functions";
import { getSaleProductIds } from "../../../../firebase/sales-functions";
import "./SideMenu.css";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

import { X } from "lucide-react";
const SideMenu = ({ menuOpen, setMenuOpen, isMobile }) => {
  const navigate = useNavigate();
  const { currentUser, logout } = useUser();
  const { language } = useLanguage();
  const [submenuOpen, setSubmenuOpen] = useState(null);

  // Helper function to get the original English category name for URLs
  const getOriginalCategoryName = (category) => {
    // Return the original name field (which is English) or the English multilingual name
    return (
      category.originalName || category.multilingual?.en?.name || category.name
    );
  };
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saleProductsCount, setSaleProductsCount] = useState(0);

  // Translation object for menu items
  const translations = {
    en: {
      home: "HOME",
      shop: "SHOP",
      sales: "SALES",
      aboutUs: "ABOUT US",
      contactUs: "CONTACT US",
      wishlist: "WISHLIST",
      myAccount: "MY ACCOUNT",
      myOrders: "MY ORDERS",
      logout: "LOGOUT",
      viewAllProducts: "View All Products",
      viewSalesProducts: "Products on Sale",
      loadingCategories: "Loading categories...",
      noCategories: "No categories available",
    },
    ar: {
      home: "الرئيسية",
      shop: "المتجر",
      sales: "التخفيضات",
      aboutUs: "من نحن",
      contactUs: "اتصل بنا",
      wishlist: "المفضلة",
      myAccount: "حسابي",
      myOrders: "طلباتي",
      logout: "تسجيل الخروج",
      viewAllProducts: "عرض جميع المنتجات",
      viewSalesProducts: "المنتجات المخفضة",
      loadingCategories: "جاري تحميل الفئات...",
      noCategories: "لا توجد فئات متاحة",
    },
  };

  const t = translations[language] || translations.en;

  // Fetch categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const storeId = DEFAULT_STORE_ID;

        // Use the centralized function to get active categories
        const fetchedCategories = await getActiveStoreCategories(storeId, {
          sortBy: "createdAt",
          sortDirection: "desc",
        });

        if (fetchedCategories.length > 0) {
          // Apply localization to the categories
          const localizedCategories = getLocalizedCategories(
            fetchedCategories,
            language
          );
          setCategories(localizedCategories);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [language]); // Re-fetch when language changes

  // Fetch sales products count
  useEffect(() => {
    const fetchSalesCount = async () => {
      try {
        const storeId = DEFAULT_STORE_ID;
        const saleProductIds = await getSaleProductIds(storeId);
        setSaleProductsCount(saleProductIds.length);
      } catch (err) {
        console.error("Error fetching sales count:", err);
        setSaleProductsCount(0);
      }
    };

    fetchSalesCount();
  }, []);

  // Navigation functions
  const goToHome = () => {
    navigate("/");
    setMenuOpen(false);
  };

  // Function to toggle submenus
  const toggleSubmenu = (index) => {
    setSubmenuOpen(submenuOpen === index ? null : index);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setMenuOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const navigateAndClose = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Faded Background Overlay */}
      <div
        className={`menu-overlay ${menuOpen ? "visible" : ""}`}
        onClick={() => setMenuOpen(false)}
      ></div>

      {/* Slide-in Side Menu */}
      <div className={`side-menu ${menuOpen ? "open" : ""}`}>
        <button className="close-side-menu" onClick={() => setMenuOpen(false)}>
          <X size={20} strokeWidth={1.5} />
        </button>

        <ul>
          <li className="menu-item show-on-all" onClick={goToHome}>
            <span>{t.home}</span>
          </li>

          <li
            className={`menu-item show-on-all ${
              submenuOpen === 0 ? "active" : ""
            }`}
            onClick={() => toggleSubmenu(0)}
          >
            <span>{t.shop}</span>
            <span className="expand-icon show-on-desktop">
              {submenuOpen === 0 ? "−" : "+"}
            </span>
          </li>

          <ul className={`submenu ${submenuOpen === 0 ? "submenu-open" : ""}`}>
            {loading ? (
              <li className="loading-item">{t.loadingCategories}</li>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/shop?category=${encodeURIComponent(
                      getOriginalCategoryName(category)
                    )}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {category.name}
                  </Link>
                </li>
              ))
            ) : (
              <li>{t.noCategories}</li>
            )}
            <li>
              <Link to="/shop" onClick={() => setMenuOpen(false)}>
                {t.viewAllProducts}
              </Link>
            </li>
          </ul>

          <li
            className="menu-item show-on-all"
            onClick={() => {
              navigate("/shop?onSale=true");
              setMenuOpen(false);
            }}
          >
            <span>
              {t.sales} {saleProductsCount > 0 && `(${saleProductsCount})`}
            </span>
          </li>

          <li
            className="menu-item show-on-all"
            onClick={() => {
              navigate("/about");
              setMenuOpen(false);
            }}
          >
            <span>{t.aboutUs}</span>
          </li>

          <li
            className="menu-item show-on-all"
            onClick={() => {
              navigate("/contact");
              setMenuOpen(false);
            }}
          >
            <span>{t.contactUs}</span>
          </li>

          {isMobile && currentUser && (
            <>
              <li
                className="menu-item"
                onClick={() => {
                  navigate("/wishlist");
                  setMenuOpen(false);
                }}
              >
                <span>{t.wishlist}</span>
              </li>
              <li
                className="menu-item"
                onClick={() => {
                  navigate("/account");
                  setMenuOpen(false);
                }}
              >
                <span>{t.myAccount}</span>
              </li>
              <li
                className="menu-item"
                onClick={() => {
                  navigate("/orders");
                  setMenuOpen(false);
                }}
              >
                <span>{t.myOrders}</span>
              </li>
              <li
                className="menu-item"
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
              >
                <span>{t.logout}</span>
              </li>
            </>
          )}
          <li className="menu-item show-on-all language-switcher-item">
            <LanguageSwitcher />
          </li>
        </ul>
      </div>
    </>
  );
};
export default SideMenu;
